const customerSections = [
  { key: 'main', title: 'main', groups: ['mainInfo', 'address', 'contactInfo', 'additionalInfo'] },
  { key: 'customer', title: 'customer', groups: ['birthInfo', 'identityData', 'additionalInfo', 'privacyConsent'] },
  { key: 'customer_accounting', title: 'customerAccounting', groups: ['mainData', 'grouping', 'expense', 'bank', 'billing'] },
  { key: 'customer_electronic_invoice', title: 'electronicInvoice', groups: ['electronicInvoiceData', 'contactInfo'] },
]

const vehicleSections = [
  { key: 'main', title: 'vehicle', groups: ['mainInfo', 'vehicleInfo'] },
  { key: 'owner', title: 'owner', groups: ['mainInfo'] },
]

export const getMappingFields = fields => fields.filter(field =>
  field.key !== 'vehicle.customer' &&
  (!field.group?.startsWith('owner.') || ['subject.name', 'subject.name_ext', 'subject.tax_code', 'subject.vat_number'].includes(field.key))
)

const hasMappingValue = (value) => value != null && (typeof value !== 'string' || value.trim() !== '')

export const isFieldMapped = (mapping) => {
  return mapping.csv_column_index != null || hasMappingValue(mapping.fixed_value) || hasMappingValue(mapping.default_value)
}

export const areMappingsComplete = (mappings) => {
  if (!mappings.length || mappings.some(row => row.required && !isFieldMapped(row))) return false
  const identifiers = mappings.filter(row => ['vehicle.plate_number', 'vehicle.vin'].includes(row.arco_field_key))
  return !identifiers.length || identifiers.some(isFieldMapped)
}

export const getMappingLayout = (trackType, trackTypes) => {
  const layouts = {
    [trackTypes.SUBJECT_CUSTOMER]: customerSections,
    [trackTypes.SUBJECT_SUPPLIER]: [
      { key: 'main', title: 'main', groups: ['mainInfo', 'address', 'contactInfo'] },
      { key: 'supplier_accounting', title: 'supplierAccounting', groups: ['mainData'] },
    ],
    [trackTypes.VEHICLE_TRANSIT]: vehicleSections,
  }
  return layouts[trackType] || []
}

export const createFieldMappings = (fields, translate, grouped = true) => {
  const mappings = fields.map(field => ({
    arco_field_key: field.key,
    label: translate(field.label),
    required: field.required,
    group: field.group,
    csv_column_index: null,
    fixed_value: null,
    default_value: null,
  }))
  if (grouped) return mappings
  return mappings.sort((a, b) =>
    (a.group || '').localeCompare(b.group || '') ||
    Number(b.required) - Number(a.required) ||
    a.label.localeCompare(b.label)
  )
}

export const mergeMappingChanges = (mappings, changesByGroup) => {
  const updates = new Map()
  for (const change of Object.values(changesByGroup).flat()) {
    updates.set(change.key, { ...updates.get(change.key), ...change.data })
  }
  return mappings.map(mapping => ({ ...mapping, ...updates.get(mapping.arco_field_key) }))
}

export const getMappingSections = (mappings, layout, translate) => {
  if (!layout.length) {
    return [{ key: 'all', title: null, groups: [{ key: 'all', title: null, rows: mappings }] }]
  }
  const rowsByGroup = new Map()
  for (const row of mappings) {
    if (!rowsByGroup.has(row.group)) rowsByGroup.set(row.group, [])
    rowsByGroup.get(row.group).push(row)
  }
  const title = (label, required) => `${translate(`common.${label}`, 2)}${required ? ' *' : ''}`
  const sections = layout.map(section => {
    const groups = section.groups.map(group => {
      const key = `${section.key}.${group}`
      const rows = rowsByGroup.get(key) || []
      rowsByGroup.delete(key)
      return { key, title: title(group, rows.some(row => row.required)), rows }
    }).filter(group => group.rows.length)
    return {
      key: section.key,
      title: title(section.title, groups.some(group => group.rows.some(row => row.required))),
      groups,
    }
  }).filter(section => section.groups.length)
  const remaining = [...rowsByGroup.values()].flat()
  if (remaining.length) {
    const contacts = [...new Set(remaining.filter(row => row.arco_field_key.startsWith('contact.')).map(row => row.arco_field_key.split('.').slice(0, 2).join('.')))]
    for (const prefix of contacts) {
      sections.push({ key: prefix, title: `${translate('common.contact')} ${prefix.split('.')[1]}`, groups: [{ key: prefix, title: null, rows: remaining.filter(row => row.arco_field_key.startsWith(prefix + '.')) }] })
    }
    const other = remaining.filter(row => !row.arco_field_key.startsWith('contact.'))
    if (other.length) {
      sections.push({
        key: 'other',
        title: title('additionalInfo', other.some(row => row.required)),
        groups: [{ key: 'other', title: null, rows: other }],
      })
    }
  }
  return sections
}
